import { HTMLAttributes, ReactNode } from 'react';

type CardProps = {
  /** Clip children to the border radius — use when a table sits flush inside. */
  flush?: boolean;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

export const Card = ({ flush, children, className = '', ...rest }: CardProps) => (
  <div
    className={`ds-card ${className}`}
    data-ds-flush={flush ? 'true' : undefined}
    {...rest}
  >
    {children}
  </div>
);

export const CardHeader = ({
  children,
  className = '',
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={`ds-card-header ${className}`} {...rest}>
    {children}
  </div>
);

export const CardBody = ({
  children,
  className = '',
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={`ds-card-body ${className}`} {...rest}>
    {children}
  </div>
);

export const CardFooter = ({
  children,
  className = '',
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={`ds-card-footer ${className}`} {...rest}>
    {children}
  </div>
);

export const Divider = ({ className = '' }: { className?: string }) => (
  <div className={`ds-divider ${className}`} role="separator" />
);

type KpiProps = {
  label: string;
  value: string;
  /** Optional trend line, e.g. "+12% vs last month". */
  delta?: string;
  direction?: 'up' | 'down' | 'flat';
  icon?: ReactNode;
};

/**
 * Compact stat tile. Deliberately small — a KPI row should summarise, not
 * dominate. Values use tabular figures so a row of numbers stays aligned.
 */
export const KpiCard = ({
  label,
  value,
  delta,
  direction = 'flat',
  icon,
}: KpiProps) => (
  <Card className="ds-kpi">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="ds-kpi-label">{label}</div>
        <div className="ds-kpi-value">{value}</div>
        {delta && (
          <div className="ds-kpi-delta" data-ds-dir={direction}>
            {delta}
          </div>
        )}
      </div>
      {icon && <div className="ds-muted shrink-0">{icon}</div>}
    </div>
  </Card>
);
